import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Sparkles, X } from 'lucide-react';
import {
  ReputationGenerationRunStatus,
  type ReputationImageStyle,
} from '@reach/shared-types/automation-template';
import { Button } from '../../../../components/ui/button';
import { DescribeVerticalStep } from './DescribeVerticalStep';
import { GenerationProgressList } from './GenerationProgressList';
import { TemplateSelectionStep } from './TemplateSelectionStep';
import {
  BACK_BUTTON_LABEL,
  CLOSE_BUTTON_LABEL,
  DIALOG_CLOSE_LABEL,
  DIALOG_DESCRIPTION,
  DIALOG_TITLE,
  DONE_BUTTON_LABEL,
  GENERATE_BUTTON_LABEL,
  GENERATE_BUTTON_PENDING_LABEL,
  GENERATION_FAILED_FALLBACK,
  GENERATION_STATUS_UNAVAILABLE_MESSAGE,
  NEXT_BUTTON_LABEL,
  PROGRESS_SUBTITLE_DONE,
  PROGRESS_SUBTITLE_RUNNING,
  SKIP_BUTTON_LABEL,
} from './reputation-defaults.constants';
import {
  useAddReputationDefaults,
  useReputationDefaults,
  useReputationGenerationStatus,
} from './useReputationDefaults';
import styles from './reputation-defaults-dialog.module.css';

type DialogStep = 'select' | 'describe' | 'progress';

interface ReputationDefaultsDialogProps {
  onClose: () => void;
  onRunStarted: (jobId: string) => void;
}

export const ReputationDefaultsDialog: React.FC<ReputationDefaultsDialogProps> = ({
  onClose,
  onRunStarted,
}) => {
  const [step, setStep] = useState<DialogStep>('select');
  /** Undefined until the partner touches the list, so the defaults can start out all selected. */
  const [chosenNames, setChosenNames] = useState<string[]>();
  const [verticalDescription, setVerticalDescription] = useState('');
  const [includeImages, setIncludeImages] = useState(false);
  const [imageStyle, setImageStyle] = useState<ReputationImageStyle>('illustration');
  const [generationRun, setGenerationRun] = useState<{ jobId: string; startedAt: number }>();

  const { data: templates } = useReputationDefaults();
  const addDefaults = useAddReputationDefaults();
  const generation = useReputationGenerationStatus(generationRun?.jobId, {
    startedAt: generationRun?.startedAt,
  });

  const addableTemplates = (templates ?? []).filter(template => !template.alreadyAdded);
  const addableNames = addableTemplates.map(template => template.name);
  const selectedNames = chosenNames ?? addableNames;

  const toggleTemplate = (name: string) =>
    setChosenNames(
      selectedNames.includes(name)
        ? selectedNames.filter(selected => selected !== name)
        : [...selectedNames, name],
    );

  const toggleAll = () =>
    setChosenNames(selectedNames.length === addableNames.length ? [] : addableNames);

  const submit = (description?: string) => {
    addDefaults.mutate(
      {
        templateNames: selectedNames,
        ...(description && { verticalDescription: description, includeImages }),
        ...(description && includeImages && { imageStyle }),
      },
      {
        onSuccess: result => {
          if (!result.generationJobId) {
            onClose();
            return;
          }
          setGenerationRun({ jobId: result.generationJobId, startedAt: Date.now() });
          onRunStarted(result.generationJobId);
          setStep('progress');
        },
      },
    );
  };

  const runFailed = generation.data?.status === ReputationGenerationRunStatus.FAILED;
  const statusUnavailable = generation.gaveUp;
  const runFinished =
    !!generation.data && generation.data.status !== ReputationGenerationRunStatus.PROCESSING;
  const stillWatching = !runFinished && !statusUnavailable;
  const progressSubtitle = stillWatching ? PROGRESS_SUBTITLE_RUNNING : PROGRESS_SUBTITLE_DONE;

  const trimmedDescription = verticalDescription.trim();
  const canGenerate = trimmedDescription.length > 0 && !addDefaults.isPending;

  return (
    <Dialog.Root defaultOpen onOpenChange={open => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.dialogOverlay} />
        <Dialog.Content className={styles.dialog} aria-modal="true">
          <header className={styles.header}>
            <div>
              <Dialog.Title asChild>
                <h3 className={styles.title}>
                  <Sparkles size={16} />
                  {DIALOG_TITLE}
                </h3>
              </Dialog.Title>
              <Dialog.Description className={step === 'progress' ? styles.subtitle : styles.srOnly}>
                {step === 'progress' ? progressSubtitle : DIALOG_DESCRIPTION}
              </Dialog.Description>
            </div>
            <Dialog.Close asChild>
              <button type="button" className={styles.close} aria-label={DIALOG_CLOSE_LABEL}>
                <X size={18} />
              </button>
            </Dialog.Close>
          </header>

          <div className={styles.body}>
            {step === 'select' && (
              <TemplateSelectionStep
                templates={templates}
                selectedNames={selectedNames}
                onToggleTemplate={toggleTemplate}
                onToggleAll={toggleAll}
              />
            )}

            {step === 'describe' && (
              <DescribeVerticalStep
                verticalDescription={verticalDescription}
                onVerticalDescriptionChange={setVerticalDescription}
                includeImages={includeImages}
                onIncludeImagesChange={setIncludeImages}
                imageStyle={imageStyle}
                onImageStyleChange={setImageStyle}
                disabled={addDefaults.isPending}
              />
            )}

            {step === 'progress' && (
              <>
                {runFailed && (
                  <p className={styles.runError}>
                    {generation.data?.error ?? GENERATION_FAILED_FALLBACK}
                  </p>
                )}
                {statusUnavailable && (
                  <p className={styles.runError}>{GENERATION_STATUS_UNAVAILABLE_MESSAGE}</p>
                )}
                <GenerationProgressList templates={generation.data?.templates ?? []} />
              </>
            )}
          </div>

          <footer className={styles.footer}>
            {step === 'select' && (
              <Button
                size="sm"
                disabled={selectedNames.length === 0}
                onClick={() => setStep('describe')}>
                {NEXT_BUTTON_LABEL} ({selectedNames.length})
              </Button>
            )}

            {step === 'describe' && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStep('select')}
                  disabled={addDefaults.isPending}>
                  {BACK_BUTTON_LABEL}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => submit()}
                  disabled={addDefaults.isPending}>
                  {SKIP_BUTTON_LABEL}
                </Button>
                <Button size="sm" onClick={() => submit(trimmedDescription)} disabled={!canGenerate}>
                  {addDefaults.isPending ? GENERATE_BUTTON_PENDING_LABEL : GENERATE_BUTTON_LABEL}
                </Button>
              </>
            )}

            {step === 'progress' && (
              <Button size="sm" onClick={onClose}>
                {stillWatching ? CLOSE_BUTTON_LABEL : DONE_BUTTON_LABEL}
              </Button>
            )}
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
